"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ProviderScheduleTable, ProviderSchedule } from "@/src/components/tasks/provider-schedule-table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { Referral } from "@/src/models/documents/referral";

// Define the location type
export interface LocationWithDistance {
  name: string;
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  distance: number;
  scheduleName: string;
}
interface USHVReferralInstructionsProps {
  referral: Referral;
  sortedLocations: LocationWithDistance[];
  schedules: ProviderSchedule[];
  receivingProvider: string | null;
  receivingLocation: string | null;
  receivingProviderOptions: string[];
  receivingLocationOptions: string[];
  nameToScheduleNameMap: Map<string, string>;
}
function customReferralString(referral: Referral): string {
  const missingFields = [];
  if (!referral.diagnosisList?.length) {
    missingFields.push("diagnosisText");
  }
  if (!referral.referringProvider?.familyName) {
    missingFields.push("referringProvider");
  }
  if (missingFields.length > 0) {
    return `referral missing:\n${missingFields.join("\n")}`;
  }
  const diagnosisText = referral.diagnosisList?.map((d: {
    diagnosisText: string | null;
  }) => d.diagnosisText).filter(Boolean).join("; ");
  return `referral from ${referral.referringProvider!.familyName} dx: ${diagnosisText}`;
}
export function USHVReferralInstructions(t0) {
  const $ = _c(38);
  const {
    referral,
    sortedLocations,
    receivingProvider,
    receivingLocation,
    schedules,
    receivingProviderOptions,
    receivingLocationOptions,
    nameToScheduleNameMap
  } = t0;
  const receivingProviderName = receivingProvider ? receivingProvider : "ANY";
  const receivingLocationName = receivingLocation ? receivingLocation : "ANY";
  let t1;
  if ($[0] !== referral) {
    t1 = customReferralString(referral);
    $[0] = referral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referralMessage = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="font-medium">Receiving Provider:</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== receivingProviderName) {
    t3 = <p>{t2}{" "}<span className="text-muted-foreground"><CopyableContent text={receivingProviderName} /></span></p>;
    $[3] = receivingProviderName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== sortedLocations) {
    t4 = sortedLocations && sortedLocations.length > 0 ? <><p className="font-medium">Closest Three Locations to Patient:</p><div className="space-y-1">{sortedLocations.map(_temp)}</div></> : <p className="text-muted-foreground">No nearby locations found</p>;
    $[5] = sortedLocations;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="font-medium">Referral Message:</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== referralMessage) {
    t6 = <p>{t5}{" "}<span className="text-muted-foreground"><CopyableContent text={referralMessage} /></span></p>;
    $[8] = referralMessage;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== nameToScheduleNameMap || $[11] !== receivingLocationName || $[12] !== receivingLocationOptions || $[13] !== receivingProviderName || $[14] !== receivingProviderOptions || $[15] !== schedules) {
    t7 = <div className="mt-4"><ProviderScheduleTable schedules={schedules} receivingLocationName={receivingLocationName} receivingProviderName={receivingProviderName} receivingProviderOptions={receivingProviderOptions} receivingLocationOptions={receivingLocationOptions} nameToScheduleNameMap={nameToScheduleNameMap} /></div>;
    $[10] = nameToScheduleNameMap;
    $[11] = receivingLocationName;
    $[12] = receivingLocationOptions;
    $[13] = receivingProviderName;
    $[14] = receivingProviderOptions;
    $[15] = schedules;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t6 || $[20] !== t7) {
    t8 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><div className="flex flex-col gap-2">{t3}{t4}{t6}{t7}</div></div></EhrInstructionsCard>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== referral.patient) {
    t9 = <PatientCard patient={referral.patient} />;
    $[22] = referral.patient;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  let t11;
  if ($[24] !== referral) {
    t10 = <ReferralProviderCards referral={referral} />;
    t11 = <ReferralCard referral={referral} isEditable={true} />;
    $[24] = referral;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  let t12;
  if ($[27] !== referral.primaryCoverage) {
    t12 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[27] = referral.primaryCoverage;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== referral.secondaryCoverage) {
    t13 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[29] = referral.secondaryCoverage;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t8 || $[36] !== t9) {
    t14 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp(location, index) {
  return <div key={location.name}><p className="font-medium">{index + 1}. {location.name} -{" "}<span className="font-normal text-muted-foreground">{location.address}</span>{" "}<span className="text-xs">(~{location.distance.toFixed(1)} miles)</span></p></div>;
}